import type { UnzipFile } from 'fflate';
import { Unzip, UnzipInflate } from 'fflate';

export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

/**
 * Streams a ZIP file chunk-by-chunk through fflate's Unzip class and yields
 * each entry as soon as it is fully decompressed. Never loads the full archive
 * into memory — safe for large ZIPs with hundreds/thousands of files.
 */
export async function* extractZip(file: File): AsyncGenerator<ZipEntry> {
  const queue: Array<ZipEntry | Error> = [];
  let done = false;
  let pendingEntries = 0;
  let streamFinished = false;
  let notify: (() => void) | null = null;

  const wakeup = () => {
    const n = notify;
    notify = null;
    n?.();
  };

  const onEntryComplete = () => {
    pendingEntries--;
    wakeup();
    if (streamFinished && pendingEntries === 0) {
      done = true;
      wakeup();
    }
  };

  const unzipper = new Unzip((stream: UnzipFile) => {
    // Skip directory entries
    if (stream.name.endsWith('/')) {
      stream.start();
      return;
    }

    pendingEntries++;
    const chunks: Uint8Array[] = [];

    stream.ondata = (err, chunk, final) => {
      if (err) {
        queue.push(
          new Error(`Error extracting "${stream.name}": ${err.message}`),
        );
        onEntryComplete();
        return;
      }
      chunks.push(chunk);
      if (final) {
        const totalLen = chunks.reduce((n, c) => n + c.length, 0);
        const data = new Uint8Array(totalLen);
        let offset = 0;
        for (const c of chunks) {
          data.set(c, offset);
          offset += c.length;
        }
        queue.push({ name: stream.name, data });
        onEntryComplete();
      }
    };

    stream.start();
  });

  // Inflate on the main thread. AsyncUnzipInflate spawns a worker from a
  // blob: URL, which the CSP (script-src 'self', no worker-src) blocks: the
  // worker never answered and every ZIP hung on « Extraction… 0 ». The photos
  // are already JPEG, so there is little to inflate anyway.
  unzipper.register(UnzipInflate);

  // Feed the File as a ReadableStream — no full arrayBuffer() load
  const reader = file.stream().getReader();
  void (async () => {
    try {
      for (;;) {
        const { done: end, value } = await reader.read();
        if (end) {
          unzipper.push(new Uint8Array(0), true);
          break;
        }
        unzipper.push(value, false);
      }
    } catch (err) {
      queue.push(err instanceof Error ? err : new Error(String(err)));
      wakeup();
    } finally {
      streamFinished = true;
      if (pendingEntries === 0) {
        done = true;
      }
      wakeup();
    }
  })();

  // Yield entries as they arrive
  for (;;) {
    while (queue.length > 0) {
      const item = queue.shift();
      if (item === undefined) {
        break;
      }
      if (item instanceof Error) {
        throw item;
      }
      yield item;
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- flipped by the reader task above, which narrowing cannot see
    if (done) {
      break;
    }
    // Wait for next entry — double-check after setting notify to avoid race condition
    await new Promise<void>((r) => {
      notify = r;
      if (queue.length > 0 || done) {
        notify = null;
        r();
      }
    });
  }
}
